import { uniq } from 'lodash'
import { useEffect, useMemo, useRef } from 'react'

import { Col, Legend, Row } from '@/components/Layout'
import { Flex } from '@/components/Layout/Flex'
import {
	Form,
	NamingInputFormField,
	SelectFormField,
	TextAreaFormField,
	TextFormField,
} from '@/components/UberForm'
import { FormlessFormField } from '@/components/UberForm/FormField'
import { getStereotypes, getSystemUsers, getUsersForCombo } from '@/endpoints'
import { useApi } from '@/endpoints/hooks'
import {
	ObjectSettingsUpdateDto,
	StereotypeDto,
	StructureObjectDto,
} from '@/endpoints/models'
import { IdCodeName, MappingDataEnums } from '@/endpoints/schemas'
import {
	useAppContext,
	useAppDispatch,
	useAppStore,
	useCustomAttributesInitValues,
} from '@/hooks'
import { CustomAttributes } from '@/pages/User/pages/Home/components'
import { MappingDataForm } from '@/store/modules/mapping/types'
import { MainContentWrap } from '@/styles'
import { booleanString } from '@/utils/booleans'
import { isInterfaceStereotype } from '@/utils/mapping'
import { uniqueNameValidator } from '@/utils/validators'

import ObjectSettingsControl from '../../../../components/ObjectSettingsControl'
import { Checkbox, FieldSetGap } from '../styles'
import { MappingOverviewProps } from '../types'
import { useNamingMapping } from './useNamingMapping'

export const MappingOverview = ({
	onChange,
	defaultMapping,
	selectedNodes,
	node,
	data: batchData,
	systemNodeId,
	editMode,
}: MappingOverviewProps) => {
	const { t } = useAppContext()
	const dispatch = useAppDispatch()
	const mappings = useAppStore((state) => state.mapping.mappings)
	const data = batchData ?? (node?.id != null ? mappings[node.id] : undefined)
	const refForm = useRef<Form<MappingDataForm>>(null)

	const isInterface =
		data?.form.defaultMappingType ===
		MappingDataEnums.MappingDefaultMappingTypeEnum.INTERFACE

	const stereotypes = useApi(
		getStereotypes({ type: StereotypeDto.TypeEnum.MAPPING }),
	)

	const [users, isUserLoading] = useApi(getUsersForCombo())

	const [systemUsers, isSystemUserLoading] = useApi(
		getSystemUsers(systemNodeId),
	)

	const handleObjectSettingsChange = (newValue: IdCodeName[]) => {
		onChange({
			objectSettings: newValue,
		})
	}

	const batchStereotypeIds = useMemo(() => {
		if (!selectedNodes?.length) {
			return undefined
		}

		return uniq(
			selectedNodes
				.map((selectedNode) => mappings[selectedNode.id]?.form.stereotypeId)
				.filter((id): id is number => id != null),
		)
	}, [mappings, selectedNodes])

	const {
		initialValuesCustomAttributes,
		getCustomAttributesReset,
		parseCustomAttribute,
		customAttributes,
	} = useCustomAttributesInitValues(
		data?.form,
		defaultMapping
			? StructureObjectDto.TypeEnum.MAPPING_DEFAULT
			: StructureObjectDto.TypeEnum.MAPPING,
		undefined,
		batchStereotypeIds,
	)

	const mappingStates = useMemo(
		() => [
			{
				label: t('MAPPING_STATUS_LOGICAL_IN_PROGRESS'),
				value: MappingDataEnums.MappingStatusEnum.LOGICAL_IN_PROGRESS,
			},
			{
				label: t('MAPPING_STATUS_LOGICAL_COMPLETED'),
				value: MappingDataEnums.MappingStatusEnum.LOGICAL_COMPLETED,
			},
			{
				label: t('MAPPING_STATUS_PHYSICAL_IN_PROGRESS'),
				value: MappingDataEnums.MappingStatusEnum.PHYSICAL_IN_PROGRESS,
			},
			{
				label: t('MAPPING_STATUS_PHYSICAL_COMPLETED'),
				value: MappingDataEnums.MappingStatusEnum.PHYSICAL_COMPLETED,
			},
			{
				label: t('MAPPING_STATUS_FROZEN'),
				value: MappingDataEnums.MappingStatusEnum.FROZEN,
			},
		],
		[t],
	)

	useEffect(() => {
		if (!data?.form.stereotypeId || !stereotypes.data) return

		const stereotype = stereotypes.data.find(
			(s) => s.id === data.form.stereotypeId,
		)

		if (stereotype && stereotype.code !== data.form.stereotypeCode) {
			onChange({
				stereotypeCode: stereotype.code,
			})
		}
	}, [
		data?.form.stereotypeCode,
		data?.form.stereotypeId,
		onChange,
		stereotypes.data,
	])

	const onChangeForm = (
		item: Partial<MappingDataForm>,
		source?: FormlessFormField<MappingDataForm>,
	) => {
		if (source) {
			const name = source.props.name

			if (name === 'responsiblePersonId' || name === 'responsibleAnalystId') {
				const user = users?.find((u) => u.id === item[name])

				const userName =
					name === 'responsiblePersonId'
						? 'responsiblePersonName'
						: 'responsibleAnalystName'

				onChange({
					[name]: user?.id,
					[userName]: user?.value,
				})

				return
			}

			if (name === 'stereotypeId') {
				const stereotype = stereotypes.data?.find((s) => s.id === item[name])

				const { customAttributesForm, customAttributesRedux } =
					getCustomAttributesReset(stereotype?.id)

				refForm.current?.setValues(customAttributesForm)

				onChange({
					stereotypeId: stereotype?.id,
					stereotypeCode: stereotype?.code,
					customAttributes: customAttributesRedux,
				})

				return
			}

			if (name === 'ownerId') {
				const owner = systemUsers?.find((u) => u.id === item[name])

				onChange({
					[name]: owner?.id,
					ownerName: owner?.name,
				})

				return
			}

			onChange(
				parseCustomAttribute(
					{
						[name]: item[name],
					},
					data?.form.customAttributes,
					customAttributes,
				),
			)
		}
	}

	const resolvedNode = node ?? selectedNodes?.[0]

	const namingConfigs = useNamingMapping(
		data?.form,
		defaultMapping,
		editMode,
		resolvedNode,
	)

	const resolvedNamingConfig =
		namingConfigs && namingConfigs?.length > 1
			? namingConfigs?.[1]
			: namingConfigs?.[0]

	return (
		<MainContentWrap>
			<Form<MappingDataForm>
				onChange={onChangeForm}
				defaultValues={{ ...data?.form, ...initialValuesCustomAttributes }}
				disabled={!editMode}
				key={booleanString(editMode)}
				enableFieldHighlight
				ref={refForm}
			>
				<Row $alignItems="flex-end">
					<Col size="medium">
						{namingConfigs === undefined || namingConfigs.length === 1 ? (
							<TextFormField
								title={t('MAPPING_NAME')}
								name="name"
								initialValue={data?.form.name}
								validators={[
									uniqueNameValidator(dispatch, data?.original.name),
								]}
								disabled={Boolean(selectedNodes)}
							/>
						) : (
							<NamingInputFormField
								node={resolvedNode}
								name="name"
								title={t('MAPPING_NAME')}
								callNamingWorkingDataParams={namingConfigs[0]}
								initialValue={data?.form.name}
								disabled={Boolean(selectedNodes)}
							/>
						)}
					</Col>
					<Col size="medium">
						<NamingInputFormField
							node={resolvedNode}
							name="code"
							title={t('MAPPING_CODE')}
							callNamingWorkingDataParams={resolvedNamingConfig}
							initialValue={data?.form.code}
							disabled={Boolean(selectedNodes)}
						/>
					</Col>
					<Col size="medium">
						<SelectFormField
							name="stereotypeId"
							title={t('MAPPING_STEREOTYPE')}
							initialValue={data?.form.stereotypeId}
							fieldIsLoading={stereotypes.reloading}
							options={
								isInterface
									? stereotypes.data?.filter((s) =>
											isInterfaceStereotype(s.code),
										)
									: stereotypes.data?.filter(
											(s) => !isInterfaceStereotype(s.code),
										) || []
							}
							valueKey="id"
							labelKey="name"
							isNumeric
							allowEmpty
							testId="mapping-stereotype"
							isDisabled={!editMode || (defaultMapping && !isInterface)}
						/>
					</Col>
					<Col size="small">
						<Flex>
							<Checkbox
								name="truncate"
								title={t('MAPPING_TRUNCATE')}
								initialValue={data?.form.truncate}
								disabled={defaultMapping}
							/>
							<Checkbox
								name="distinct"
								title={t('MAPPING_DISTINCT')}
								initialValue={data?.form.distinct}
								disabled={defaultMapping}
							/>
						</Flex>
					</Col>
				</Row>

				<Row>
					<Col size="big">
						<TextAreaFormField
							name="description"
							title={t('MAPPING_DESCRIPTION')}
							testId="overview-description"
							initialValue={data?.form.description}
							rows={5}
						/>
					</Col>
					<Col size="big">
						<TextAreaFormField
							name="comment"
							title={t('MAPPING_COMMENT')}
							initialValue={data?.form.comment}
							rows={5}
						/>
					</Col>
				</Row>
				<ObjectSettingsControl
					name="objectSettings"
					label={t('OBJECT_SETTINGS')}
					onChange={handleObjectSettingsChange}
					disabled={!editMode}
					data={data?.form.objectSettings}
					type={ObjectSettingsUpdateDto.StructureTypeEnum.MAPPING}
				/>
				<FieldSetGap>
					<Legend>{t('TABLE_GENERAL_PROJECT_METADATA')}</Legend>
					<Row>
						<Col size="medium">
							<SelectFormField
								name="status"
								title={t('MAPPING_STATUS')}
								initialValue={data?.form.status}
								options={mappingStates}
								valueKey="value"
								labelKey="label"
								isDisabled={!editMode}
							/>
						</Col>
						<Col size="medium">
							<SelectFormField
								name="responsiblePersonId"
								title={t('RESPONSIBLE_PERSON')}
								options={users || []}
								fieldIsLoading={isUserLoading}
								valueKey="id"
								labelKey="value"
								initialValue={data?.form.responsiblePersonId}
								isNumeric
								allowEmpty
								isDisabled={!editMode}
							/>
						</Col>
						<Col size="medium">
							<SelectFormField
								name="responsibleAnalystId"
								title={t('RESPONSIBLE_ANALYST')}
								options={users || []}
								fieldIsLoading={isUserLoading}
								valueKey="id"
								labelKey="value"
								initialValue={data?.form.responsibleAnalystId}
								isNumeric
								allowEmpty
								isDisabled={!editMode}
							/>
						</Col>
					</Row>
				</FieldSetGap>
				<FieldSetGap>
					<Legend>{t('TABLE_GENERAL_TECHNICAL_METADATA')}</Legend>
					<Row>
						<Col size="medium">
							<SelectFormField
								name="ownerId"
								title={t('NODE_OWNER')}
								options={systemUsers || []}
								fieldIsLoading={isSystemUserLoading}
								initialValue={data?.form.ownerId}
								valueKey="id"
								labelKey="name"
								isNumeric
								allowEmpty
								clearable
								isDisabled={!editMode}
							/>
						</Col>
					</Row>
				</FieldSetGap>
				<CustomAttributes customAttributes={customAttributes} />
			</Form>
		</MainContentWrap>
	)
}
